import { storeToRefs } from 'pinia';
import { useHAlerts } from './use-alerts';
import { useOUserSettingsStore } from '../stores/use-user-settings-store';
import { useHConfig } from './use-helix-config';
import { useHPageHead } from './use-page-head';
import { useHBindQueryParams } from './use-bind-query-params';
import { useHFetch } from './http/use-http-request';
import { useHErrorRedirect } from './use-error-redirect';
import { isAbsoluteUrl } from '../utils/http-utils';

function getUser() {
  const { oUserSettings } = storeToRefs(useOUserSettingsStore());

  return oUserSettings;
}

// For optimization
/**
 * A Vue composable that provides common Helix tools.
 *
 * @function
 * @name useHCommon
 * @description
 * This composable provides a set of common tools used across the Helix application. It uses Vue's dependency injection
 * to retrieve instances of various utilities and returns them through a proxy object. The proxy ensures that the tools
 * are lazily instantiated when accessed.
 *
 * @returns {Object} An object containing the following tools:
 * - `hAlerts`: An instance of the alerts composable.
 * - `hError`: An instance of the error redirect composable.
 * - `hConfig`: An instance of the configuration composable.
 * - `hHttpRequest`: An instance of the HTTP request composable.
 * - `hPageTitle`: The page title composable.
 * - `hBindQueryParams`: An instance of the query parameters binding composable.
 * - `hGetUrl`: The URL parsing utility.
 */
export function useHCommon() {
  const { hPageTitle } = useHPageHead();

  type HelixCommonTools = {
    hAlerts: ReturnType<typeof useHAlerts>;
    hError: ReturnType<typeof useHErrorRedirect>;
    hConfig: ReturnType<typeof useHConfig>;
    hHttpRequest: ReturnType<typeof useHFetch>;
    hPageTitle: typeof hPageTitle;
    hBindQueryParams: ReturnType<typeof useHBindQueryParams>;
    hGetUrl: typeof getUrl;
  };

  const helixCommonToolsProxy = new Proxy<HelixCommonTools>(
    {} as any, // TODO: Is there a way to improve this?
    {
      get(_, prop) {
        const key = prop as keyof HelixCommonTools;

        if (key === 'hAlerts') {
          return useHAlerts();
        } else if (key === 'hHttpRequest') {
          return useHFetch();
        } else if (key === 'hConfig') {
          return useHConfig();
        } else if (key === 'hPageTitle') {
          return hPageTitle;
        } else if (key === 'hBindQueryParams') {
          return useHBindQueryParams;
        } else if (key === 'hError') {
          return useHErrorRedirect();
        } else if (key === 'hGetUrl') {
          return getUrl;
        }
        return undefined;
      },
    },
  );

  return helixCommonToolsProxy;
}

function getUrl(url: string) {
  const { hubAppUrl } = useHConfig();
  return `${isAbsoluteUrl(url) ? '' : hubAppUrl}${url}`;
}

/**
 * Retrieves the appropriate image URL based on the environment.
 *
 * @function
 * @name getImage
 * @param {string} path - The relative path to the image.
 * @returns {string} - The full URL to the image. If in a development environment, returns an absolute URL using `hubAppUrl`. If in a production environment, returns the relative path.
 * @description
 * This function determines the correct URL for an image based on whether the application is running in a production or development environment. In development, it constructs an absolute URL using the `hubAppUrl`. In production, it returns the relative path.
 */
export function getImage(path: string) {
  const { isProduction, hubAppUrl } = useHConfig();

  // We check if we're working in a development environment here
  if (!isProduction) {
    // We are in a development enviornment so we must use an absolute url to get our icons. We append `iconPath` to `hub-app` url to get icons hosted there.
    return `${hubAppUrl}${path}`;
  } else {
    // We are in a production environment, we can use relative paths to get our icons
    return path;
  }
}

/**
 * A Vue composable that provides common tools for Onify.
 *
 * @function
 * @name useOCommon
 * @description
 * This composable returns a proxy object that provides access to common tools used in Onify.
 * It includes methods to get the current user and to get images. If the requested tool is not
 * found in the Onify tools, it falls back to the common tools provided by Helix.
 *
 * @typedef {Object} OnifyCommonTools
 * @property {Function} oUser - A function that returns the current user.
 * @property {Function} oGetImage - A function to get images.
 *
 * @returns {Proxy<OnifyCommonTools>} A proxy object that provides access to Onify common tools.
 */
export function useOCommon() {
  type OnifyCommonTools = {
    oUser: ReturnType<typeof getUser>;
    oGetImage: typeof getImage;
  };

  const onifyToolsProxy = new Proxy<OnifyCommonTools>(
    {} as any, // TODO: Is there a way to improve this?
    {
      get(_, prop) {
        const key = prop as keyof OnifyCommonTools;

        if (key === 'oUser') {
          return getUser();
        } else if (key === 'oGetImage') {
          return getImage;
        }

        const helixCommonTools = useHCommon();
        return helixCommonTools[key];
      },
    },
  );

  return onifyToolsProxy;
}
